/**
 * Deals query hooks using generic factory
 */

import type { DealFilters } from '@/store/deal-filters.store'
import { dealApi } from '@/lib/api/deals.api'
import { useOrganizationStore } from '@/store/organization.store'
import { useQuery } from '@tanstack/react-query'

import type { Deal, PaginatedResponse } from '@scrambl/shared'

import { queryKeys } from '../query-keys'
import { createResourceQueries } from './createResourceQuery'

/**
 * Adapter to normalize dealsApi methods to expected interface
 */
const dealsQueryConfig = {
  queryKeyNamespace: queryKeys.deals.all,
  api: {
    getList: (filters?: DealFilters) => {
      const params: Record<string, unknown> = {
        page: filters?.page || 1,
        limit: filters?.limit || 50,
        sortBy: filters?.sortBy,
        sortOrder: filters?.sortOrder,
      }

      if (filters?.name && filters.name.trim() !== '') {
        params.name = filters.name.trim()
      }

      // Add stage filter if it exists and is not empty
      if (filters?.stage && (filters.stage as string) !== '') {
        params.stage = filters.stage
      }

      // Add stageTypeId filter if it exists and is not empty
      if (filters?.stageTypeId && (filters.stageTypeId as string) !== '') {
        params.stageTypeId = filters.stageTypeId
      }

      if (filters?.contactId) {
        params.contactId = filters.contactId
      }

      // T2 record-state opt-in: unset = server default (active + provisional,
      // merged/archived tombstones hidden); 'all' or an explicit state opts in.
      if (filters?.recordState) {
        params.recordState = filters.recordState
      }

      // C13/T7 divergence-only opt-in: server keeps only deals whose rep-vs-
      // derived probability gap is large (either direction).
      if (filters?.divergenceOnly) {
        params.divergenceOnly = true
      }

      if (filters?.closeDateFrom) params.closeDateFrom = filters.closeDateFrom
      if (filters?.closeDateTo) params.closeDateTo = filters.closeDateTo

      // Add column filters if they exist (for server-side filtering)
      if (
        filters?.columnFilters &&
        Object.keys(filters.columnFilters).length > 0
      ) {
        params.columnFilters = filters.columnFilters
      }

      return dealApi.getDeals(params)
    },
    getDetail: (id: string) => dealApi.getDeal(id),
  },
  requiresOrganization: true,
}

/**
 * Generate all deal query hooks using the factory
 */
const { useListQuery, useDetailQuery } = createResourceQueries<
  Deal,
  DealFilters
>(dealsQueryConfig, queryKeys.deals)

/**
 * Hook to fetch deals with filters
 */
export const useDealsQuery = useListQuery

/**
 * Hook to fetch a single deal by ID
 */
export const useDealQuery = useDetailQuery

/**
 * Hook to fetch deals for a specific contact
 */
export function useDealsByContactQuery(
  contactId: string | undefined,
  filters?: {
    limit?: number
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<PaginatedResponse<Deal>>({
    queryKey: queryKeys.deals.byContact(contactId || '', filters),
    queryFn: () =>
      dealApi.getDealsByContactId(contactId!, { limit: filters?.limit }),
    enabled:
      filters?.enabled !== false && !!contactId && !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

/**
 * useDealsQuery under a non-hook name for useModuleTable configs: passing a
 * use*-named import as a value makes React Compiler bail out of the consuming
 * component. Same function, only ever called unconditionally inside the hook.
 */
export const dealsListQuery = useDealsQuery
